import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Search,
  Book,
  Rocket,
  Settings,
  CreditCard,
  Users,
  Webhook,
  Key,
  Shield,
  Mail,
  HelpCircle,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import MorphingNavbar from '../../components/MorphingNavbar';

const docCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    description: 'Quick setup guides to get your portal running',
    articles: [
      { title: 'Quick Start Guide', slug: 'quick-start', readTime: '5 min' },
      { title: 'Creating Your First Service', slug: 'first-service', readTime: '3 min' },
      { title: 'Adding Your First Client', slug: 'first-client', readTime: '2 min' },
      { title: 'Setting Up Payments', slug: 'setup-payments', readTime: '5 min' },
      { title: 'Customizing Your Portal', slug: 'customization', readTime: '4 min' }
    ]
  },
  {
    id: 'client-management',
    title: 'Client Management',
    icon: Users,
    description: 'Managing clients, services, and orders',
    articles: [
      { title: 'Client Dashboard Overview', slug: 'client-dashboard', readTime: '3 min' },
      { title: 'Managing Client Services', slug: 'client-services', readTime: '4 min' },
      { title: 'Client Communication', slug: 'client-communication', readTime: '3 min' },
      { title: 'Client Self-Service Portal', slug: 'self-service', readTime: '5 min' }
    ]
  },
  {
    id: 'payments',
    title: 'Payments & Billing',
    icon: CreditCard,
    description: 'Stripe integration, invoicing, and subscriptions',
    articles: [
      { title: 'Connecting Stripe', slug: 'stripe-connect', readTime: '5 min' },
      { title: 'Creating Checkout Sessions', slug: 'checkout', readTime: '4 min' },
      { title: 'Managing Subscriptions', slug: 'subscriptions', readTime: '5 min' },
      { title: 'Refunds & Disputes', slug: 'refunds', readTime: '3 min' }
    ]
  },
  {
    id: 'white-label',
    title: 'White-Label Setup',
    icon: Settings,
    description: 'Custom domains, branding, and email',
    articles: [
      { title: 'Custom Domain Setup', slug: 'custom-domain', readTime: '5 min' },
      { title: 'Branding Your Portal', slug: 'branding', readTime: '4 min' },
      { title: 'Email Configuration', slug: 'email-config', readTime: '5 min' },
      { title: 'SSL Certificate Management', slug: 'ssl', readTime: '3 min' }
    ]
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: Webhook,
    description: 'Webhooks, API, and third-party connections',
    articles: [
      { title: 'Webhooks Overview', slug: 'webhooks-overview', readTime: '4 min' },
      { title: 'Available Webhook Events', slug: 'webhook-events', readTime: '6 min' },
      { title: 'Testing Webhooks', slug: 'webhook-testing', readTime: '3 min' },
      { title: 'Zapier Integration', slug: 'zapier', readTime: '5 min' }
    ]
  },
  {
    id: 'api',
    title: 'API Reference',
    icon: Key,
    description: 'REST API documentation for developers',
    articles: [
      { title: 'Authentication', slug: 'api-auth', readTime: '4 min' },
      { title: 'Clients API', slug: 'api-clients', readTime: '5 min' },
      { title: 'Services API', slug: 'api-services', readTime: '5 min' },
      { title: 'Orders API', slug: 'api-orders', readTime: '5 min' },
      { title: 'Rate Limits', slug: 'api-rate-limits', readTime: '2 min' }
    ]
  },
  {
    id: 'security',
    title: 'Security & Compliance',
    icon: Shield,
    description: 'GDPR, data protection, and security features',
    articles: [
      { title: 'Security Overview', slug: 'security-overview', readTime: '5 min' },
      { title: 'GDPR Compliance', slug: 'gdpr', readTime: '6 min' },
      { title: 'Data Export & Deletion', slug: 'data-export', readTime: '4 min' },
      { title: 'Two-Factor Authentication', slug: '2fa', readTime: '3 min' }
    ]
  },
  {
    id: 'email',
    title: 'Email & Notifications',
    icon: Mail,
    description: 'Automated emails and notification settings',
    articles: [
      { title: 'Email Provider Setup', slug: 'email-provider', readTime: '5 min' },
      { title: 'Notification Types', slug: 'notification-types', readTime: '4 min' },
      { title: 'Email Templates', slug: 'email-templates', readTime: '5 min' },
      { title: 'Delivery Troubleshooting', slug: 'email-troubleshooting', readTime: '4 min' }
    ]
  }
];

const popularArticles = [
  { title: 'Quick Start Guide', category: 'Getting Started', slug: 'quick-start' },
  { title: 'Connecting Stripe', category: 'Payments', slug: 'stripe-connect' },
  { title: 'Custom Domain Setup', category: 'White-Label', slug: 'custom-domain' },
  { title: 'Webhooks Overview', category: 'Integrations', slug: 'webhooks-overview' }
];

export default function Docs() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const filteredCategories = searchQuery
    ? docCategories.filter(cat => 
        cat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.articles.some(art => art.title.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : docCategories;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <MorphingNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 py-16" data-testid="docs-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            How can we help?
          </h1>
          <p className="text-slate-300 mb-8">
            Search our knowledge base or browse categories below
          </p>
          
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search documentation..."
              className="w-full pl-12 pr-4 h-14 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/20"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="docs-search"
            />
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      {!searchQuery && (
        <section className="py-8 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              <span className="text-sm font-medium text-slate-500 whitespace-nowrap">Popular:</span>
              {popularArticles.map((article, index) => (
                <Link
                  key={index}
                  to={`/docs/${article.slug}`}
                  className="px-4 py-2 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-full text-sm font-medium text-slate-600 whitespace-nowrap transition-colors"
                  data-testid={`popular-article-${index}`}
                >
                  {article.title}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Grid */}
      <section className="py-12" data-testid="docs-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="p-6 bg-white rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                data-testid={`doc-category-${category.id}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center mb-4 group-hover:from-indigo-500 group-hover:to-violet-500 transition-colors">
                  <category.icon className="w-6 h-6 text-indigo-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{category.title}</h3>
                <p className="text-sm text-slate-500 mb-4">{category.description}</p>
                <span className="text-sm text-indigo-600 font-medium">
                  {category.articles.length} articles
                  <ChevronRight className="w-4 h-4 inline ml-1" />
                </span>

                {/* Expanded Articles List */}
                {selectedCategory === category.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <ul className="space-y-2">
                      {category.articles.map((article, index) => (
                        <li key={index}>
                          <Link
                            to={`/docs/${article.slug}`}
                            className="flex items-center justify-between py-2 px-3 -mx-3 rounded-lg hover:bg-slate-50 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-sm text-slate-700">{article.title}</span>
                            <span className="text-xs text-slate-400">{article.readTime}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-white" data-testid="docs-quick-links">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Quick Links</h2>
          
          <div className="grid sm:grid-cols-3 gap-6">
            <a 
              href="mailto:support@edrano.my"
              className="p-6 rounded-xl border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 transition-all flex items-start gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Contact Support</h3>
                <p className="text-sm text-slate-500">Get help from our team</p>
              </div>
            </a>

            <Link 
              to="/docs/api-auth"
              className="p-6 rounded-xl border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 transition-all flex items-start gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">API Reference</h3>
                <p className="text-sm text-slate-500">Full REST API documentation</p>
              </div>
            </Link>

            <a 
              href="https://status.edrano.my"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 rounded-xl border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 transition-all flex items-start gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                  System Status
                  <ExternalLink className="w-4 h-4 text-slate-400" />
                </h3>
                <p className="text-sm text-slate-500">Check platform uptime</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Edrano. All rights reserved.
              <span className="mx-2">•</span>
              Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Themelio</a>
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-indigo-600">Privacy</Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-indigo-600">Terms</Link>
              <a href="mailto:support@edrano.my" className="text-sm text-slate-500 hover:text-indigo-600">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
